import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthenticationService } from 'src/app/core/services/authentication/authentication.service';
import { RegisterService } from 'src/app/core/services/register/register.service';
import { BusinessRepresentativeFormObject } from 'src/app/shared/models/business-representative/business-representative-form-object/business-representative-form-object';
import { LocalStorageKeys } from 'src/app/shared/models/constants/storage.keys';
import { HashValidationDTO } from 'src/app/shared/models/dtos/responses/hash-validation-dto/hash-validation-dto';
import { RoutesKeys } from 'src/app/utils/routes';
import { RecaptchaComponent } from 'src/app/shared/components/molecules/recaptcha/recaptcha.component';
import { environment } from '../../../environments/environment';

@Component({
  templateUrl: './create-account.page.html',
})
/**
 * Instantiates create account template
 * The page is shown after the invitation of a new business representative
 */
export class CreateAccountPage implements OnInit {
  businessRepresentative!: BusinessRepresentativeFormObject;
  termsFormGroup!: FormGroup;
  loading = false;
  errorMsg = '';
  recaptchaFormGroup!: FormGroup;
  hashValidationDTO: HashValidationDTO | null = null;
  @ViewChild('captchaRef') captchaRef?: RecaptchaComponent;
  recaptchaEnabled = environment.recaptcha.enabled;

  /**
   * @constructor
   */
  constructor(
    private registerService: RegisterService,
    private router: Router,
    private authenticationService: AuthenticationService
  ) {}

  /**
   * ngOnInit
   */
  ngOnInit(): void {
    this.businessRepresentative =
      this.registerService.businessRepresentativeFormObject;
    const knownEmail = this.authenticationService.passwordResetEmail;
    if (this.registerService.inviteNewBusiness == null || !knownEmail) {
      this.router.navigate([RoutesKeys.ONBOARD]);
      return;
    }
    const emailFormControl =
      this.businessRepresentative.formGroup.get('email')!;
    emailFormControl.setValue(knownEmail);
    emailFormControl.disable();

    this.termsFormGroup = new FormGroup({
      agree: new FormControl(false, [Validators.requiredTrue]),
    });
    this.registerService.getTermsOfUseAndPrivacyConditionsURLs().subscribe({
      next: (res: any) => {
        this.businessRepresentative.termsAndConditionsLink = res.termsOfUseURL;
        this.businessRepresentative.privacyPolicyLink =
          res.privacyConditionsURL;
      },
      error: () => {
        this.termsFormGroup.disable();
      },
    });
    this.recaptchaFormGroup = new FormGroup({
      recaptcha: new FormControl(
        null,
        this.recaptchaEnabled ? Validators.required : []
      ),
    });
    this.registerService.getHashValidationDTO().subscribe((data) => {
      this.hashValidationDTO = data;
    });
  }

  /**
   * Create account (TBD)
   */
  async onCreateAccount() {
    this.loading = true;

    const profileName =
      this.businessRepresentative.formGroup.get('name')!.value;
    const { recaptcha } = this.recaptchaFormGroup.getRawValue();
    const recaptchaToken = this.recaptchaEnabled ? recaptcha : 'dev-bypass';
    try {
      await this.registerService.registerRepresentative(
        recaptchaToken,
        this.hashValidationDTO?.invitedBusiness.country!.language
      );
    } catch (e) {
      this.errorMsg = (e as Error).message;
      return;
    } finally {
      this.loading = false;
      if (this.recaptchaEnabled) {
        this.captchaRef?.reset();
      }
    }

    const image = document.getElementById(
      'image-file-input'
    ) as HTMLImageElement;

    if (image) {
      localStorage.setItem(LocalStorageKeys.PROFILE_PHOTO, image.src);
    }
    localStorage.setItem(LocalStorageKeys.FIRST_NAME, profileName);
    this.router.navigate([RoutesKeys.PROFILE_CREATED]);
  }

  /**
   * Checks wether the button must be disabled or not
   * @return {boolean}
   */
  checkDisabled() {
    return (
      this.businessRepresentative.formGroup.invalid ||
      this.termsFormGroup.invalid ||
      (this.recaptchaEnabled && this.recaptchaFormGroup.invalid)
    );
  }
}
